/**
 * Personal Records service layer.
 * Handles detection, storage, and retrieval of personal records (PRs)
 * after workout completion. PRs are stored per exercise per user.
 *
 * PR types tracked:
 * - maxWeight: Heaviest weight lifted for any rep count
 * - maxVolume: Highest single-set volume (weight x reps)
 * - maxReps: Most reps at any weight (useful for bodyweight exercises)
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const PR_COLLECTION = 'personalRecords';

/**
 * Build a Firestore-safe document ID from an exercise name.
 * @param {string} exerciseName
 * @returns {string}
 */
const toDocId = (exerciseName) =>
  (exerciseName || '').toLowerCase().replace(/[^a-z0-9]/g, '_');

/**
 * Check all exercises in a completed workout for new PRs and update storage.
 * Returns an array of new PR objects for celebration display.
 *
 * @param {string} userId - The user's ID.
 * @param {Object} workout - The completed workout data.
 * @returns {Promise<Array<{exerciseName: string, type: string, value: number, unit: string}>>}
 */
export const checkAndUpdatePRs = async (userId, workout) => {
  try {
    const newPRs = [];
    const exercises = workout.exercises || [];

    for (const exercise of exercises) {
      const exerciseName = exercise.name;
      if (!exerciseName) continue;

      const docId = `${userId}_${toDocId(exerciseName)}`;
      const docRef = doc(db, PR_COLLECTION, docId);
      const docSnap = await getDoc(docRef);
      const existing = docSnap.exists() ? docSnap.data() : null;

      let updated = false;
      const updates = {
        exerciseName,
        userId,
        maxWeight: existing?.maxWeight || null,
        maxVolume: existing?.maxVolume || null,
        maxReps: existing?.maxReps || null,
        history: existing?.history || [],
      };

      for (const set of exercise.sets || []) {
        const weight = parseFloat(set.weight) || 0;
        const reps = parseInt(set.reps, 10) || 0;
        const volume = weight * reps;
        const unit = set.unit || 'lbs';

        // Check max weight PR
        if (weight > 0 && (!updates.maxWeight || weight > updates.maxWeight.value)) {
          updates.maxWeight = {
            value: weight,
            unit,
            reps,
            date: new Date().toISOString(),
            workoutId: workout.id || null,
          };
          updates.history = [
            { type: 'weight', value: weight, date: new Date().toISOString() },
            ...updates.history,
          ].slice(0, 10);
          newPRs.push({ exerciseName, type: 'weight', value: weight, unit });
          updated = true;
        }

        // Check max volume PR
        if (volume > 0 && (!updates.maxVolume || volume > updates.maxVolume.value)) {
          updates.maxVolume = {
            value: volume,
            unit,
            weight,
            reps,
            date: new Date().toISOString(),
            workoutId: workout.id || null,
          };
          updates.history = [
            { type: 'volume', value: volume, date: new Date().toISOString() },
            ...updates.history,
          ].slice(0, 10);
          if (!newPRs.find((p) => p.exerciseName === exerciseName && p.type === 'volume')) {
            newPRs.push({ exerciseName, type: 'volume', value: volume, unit });
          }
          updated = true;
        }

        // Check max reps PR
        if (reps > 0 && (!updates.maxReps || reps > updates.maxReps.value)) {
          updates.maxReps = {
            value: reps,
            weight,
            unit,
            date: new Date().toISOString(),
            workoutId: workout.id || null,
          };
          updates.history = [
            { type: 'reps', value: reps, date: new Date().toISOString() },
            ...updates.history,
          ].slice(0, 10);
          if (!newPRs.find((p) => p.exerciseName === exerciseName && p.type === 'reps')) {
            newPRs.push({ exerciseName, type: 'reps', value: reps, unit });
          }
          updated = true;
        }
      }

      if (updated) {
        updates.updatedAt = new Date().toISOString();
        await setDoc(docRef, updates, { merge: true });
      }
    }

    return newPRs;
  } catch (error) {
    handleServiceError(error, 'Check PRs', 'Failed to check personal records.');
    return [];
  }
};

/**
 * Get all personal records for a user.
 * @param {string} userId
 * @returns {Promise<Array>}
 */
export const getPRs = async (userId) => {
  try {
    const q = query(
      collection(db, PR_COLLECTION),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    handleServiceError(error, 'Get PRs', 'Failed to load personal records.');
    return [];
  }
};

/**
 * Get PR data for a specific exercise.
 * @param {string} userId
 * @param {string} exerciseName
 * @returns {Promise<Object|null>}
 */
export const getPRForExercise = async (userId, exerciseName) => {
  try {
    const docId = `${userId}_${toDocId(exerciseName)}`;
    const docRef = doc(db, PR_COLLECTION, docId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    handleServiceError(error, 'Get exercise PR', 'Failed to load personal record.');
    return null;
  }
};

/**
 * Get the most recent PRs across all exercises.
 * @param {string} userId
 * @param {number} [max=10]
 * @returns {Promise<Array>}
 */
export const getRecentPRs = async (userId, max = 10) => {
  try {
    const allPRs = await getPRs(userId);
    // Flatten history entries with exercise context
    const recentEntries = [];

    allPRs.forEach((pr) => {
      (pr.history || []).forEach((h) => {
        recentEntries.push({
          exerciseName: pr.exerciseName,
          type: h.type,
          value: h.value,
          date: h.date,
          maxWeight: pr.maxWeight,
          maxVolume: pr.maxVolume,
          maxReps: pr.maxReps,
        });
      });
    });

    recentEntries.sort((a, b) => new Date(b.date) - new Date(a.date));
    return recentEntries.slice(0, max);
  } catch (error) {
    handleServiceError(error, 'Get recent PRs', 'Failed to load recent records.');
    return [];
  }
};
